import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import Modal, { Field, inputCls, btnPrimary, btnGhost } from "@/components/Modal";
import { fmtDate } from "@/lib/format";
import { Plus, Users, Shield, CheckCircle2, XCircle } from "lucide-react";

export default function Cashiers() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  const load = async () => {
    const { data } = await api.get("/cashiers");
    setList(data);
  };
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEdit(null);
    setForm({ name: "", email: "", password: "" });
    setOpen(true);
  };
  const openEdit = (u) => {
    setEdit(u);
    setForm({ name: u.name, email: u.email, password: "" });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (edit) {
        await api.put(`/cashiers/${edit.id}`, { name: form.name, password: form.password || undefined });
      } else {
        if (!form.email || !form.password) return toast.error("Email & password required");
        await api.post("/cashiers", form);
      }
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/cashiers/${u.id}`, { active: !u.active });
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    try {
      await api.delete(`/cashiers/${u.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-head text-2xl font-bold">Cashiers</h1>
          <p className="text-sm text-[#6B7280]">Manage staff accounts</p>
        </div>
        <button data-testid="add-cashier-btn" onClick={openNew} className={btnPrimary + " flex items-center gap-2"}>
          <Plus className="w-4 h-4" /> Add Cashier
        </button>
      </div>

      <div className="bg-[#151D2A] border border-[#23334D] rounded-xl divide-y divide-[#23334D]">
        {list.map((u) => (
          <div key={u.id} data-testid={`cashier-row-${u.id}`} className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#1E2C45] flex items-center justify-center">
                {u.role === "admin" ? <Shield className="w-4 h-4 text-[#F59E0B]" /> : <Users className="w-4 h-4 text-[#06B6D4]" />}
              </div>
              <div>
                <div className="font-medium flex items-center gap-2">
                  {u.name}
                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#1E2C45] text-[#9CA3AF]">{u.role}</span>
                </div>
                <div className="text-xs text-[#6B7280]">{u.email}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {u.role !== "admin" && (
                <button onClick={() => toggleActive(u)} className="text-xs flex items-center gap-1" data-testid={`toggle-cashier-${u.id}`}>
                  {u.active ? <CheckCircle2 className="w-4 h-4 text-[#10B981]" /> : <XCircle className="w-4 h-4 text-[#EF4444]" />}
                  <span className={u.active ? "text-[#10B981]" : "text-[#EF4444]"}>{u.active ? "Active" : "Disabled"}</span>
                </button>
              )}
              {u.role !== "admin" && (
                <>
                  <button onClick={() => openEdit(u)} className="text-sm text-[#9CA3AF] hover:text-white">Edit</button>
                  <button onClick={() => del(u)} data-testid={`delete-cashier-${u.id}`} className="text-sm text-[#9CA3AF] hover:text-[#EF4444]">Delete</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={edit ? "Edit Cashier" : "New Cashier"}
        footer={
          <>
            <button onClick={() => setOpen(false)} className={btnGhost}>Cancel</button>
            <button onClick={save} data-testid="save-cashier-btn" className={btnPrimary}>Save</button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Name">
            <input className={inputCls} value={form.name} data-testid="cashier-name-input" onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
          </Field>
          <Field label="Email">
            <input className={inputCls} type="email" value={form.email} disabled={!!edit} data-testid="cashier-email-input" onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label={edit ? "New Password (leave blank to keep)" : "Password"}>
            <input className={inputCls} type="text" value={form.password} data-testid="cashier-password-input" onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </Field>
        </div>
      </Modal>
    </div>
  );
}
